import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Upload } from 'lucide-react';
import { getVnStatus, requestVnLink, openVnLink, getVnRequestData } from '../../lib/vorgangApi';
import { API_BASE } from '../../lib/apiBase';

type VnStatus = 'offen' | 'wartet' | 'bereit';

interface VnButtonProps {
  vorgangId: number;
  customerId: number;
  /** Called when the accordion is opened — triggers initial status load */
  active: boolean;
  /** True, wenn bereits ein (roher, unsignierter) Verwendungsnachweis im Vorgang liegt.
   *  Dann ist „VN-Link anfordern" überflüssig (Formular ist schon da) → Button ausblenden. */
  vnUploaded?: boolean;
  /** 'menu' → volle Breite im „⋯"-Menü des Vorgangs statt eigenständiger Knopf. */
  variant?: 'inline' | 'menu';
  /** Meldet den Link-Status nach oben, damit das „⋯"-Menü einen abholbereiten Link
   *  anzeigen kann, ohne dass der Knopf dafür seinen Platz wechselt (Remount = neuer Abruf). */
  onStatus?: (s: VnStatus | null) => void;
}

export function VnButton({ vorgangId, customerId, active, vnUploaded, variant = 'inline', onStatus }: VnButtonProps) {
  const [status, setStatus] = useState<VnStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);
  const [linkValidUntil, setLinkValidUntil] = useState<string | null>(() => {
    // Beim Mount: linkValidUntil aus localStorage laden (bleibt nach Browser-Reload erhalten)
    try {
      return localStorage.getItem(`vnLinkValidUntil_${vorgangId}`) || null;
    } catch {
      return null;
    }
  });
  // Prüf-Dialog vor dem Anfordern: die drei Werte, mit denen sich der Server bei der BAFA
  // anmeldet, werden vorgeblendet und sind korrigierbar. Korrekturen gelten NUR für diese
  // Anfrage (die Stammdaten bleiben unangetastet) — der VN kann bei der BAFA z.B. mit einer
  // anderen E-Mail angelegt worden sein.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [form, setForm] = useState({ vorgangsnummer: '', plz: '', email: '' });
  // true → die vorgeblendete E-Mail ist die am Vorgang gemerkte BAFA-Adresse.
  const [emailFromBafaFeld, setEmailFromBafaFeld] = useState(false);
  // Hinweis-Mail an den Kunden: nötig, wenn die hinterlegte Adresse keine vom Berater
  // überwachte Mailbox ist — der Einmal-Link der BAFA ginge sonst unbemerkt an den Kunden.
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  // Erzwingt beim Warten auf die BAFA-Mail einen Re-Render pro Sekunde, damit der Countdown
  // unter dem gelben Knopf tickt (linkValidUntil selbst ändert sich nur alle 30s per Poll).
  const [, forceTick] = useState(0);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Merkt, ob der Mousedown, der zu diesem Klick führte, direkt auf dem Overlay-Hintergrund
  // begann — sonst schließt eine Textmarkierung, die über den Dialogrand hinausgezogen wird
  // (Mousedown im Feld, Mouseup auf dem Hintergrund), fälschlich den Dialog.
  const backdropPressRef = useRef(false);
  // Tracks whether the component is still mounted and the current effect is active.
  // Stored as a ref so interval callbacks always see the latest value.
  const cancelledRef = useRef(false);

  const stopPolling = () => {
    if (pollingRef.current != null) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  // Fetches status from API and returns it — does NOT call setState itself.
  // Callers guard against unmount/cancellation via cancelledRef.
  const fetchStatus = async (): Promise<VnStatus | null> => {
    try {
      const res = await getVnStatus(vorgangId);
      if (!cancelledRef.current) {
        const validUntil = res.linkValidUntil ?? null;
        setLinkValidUntil(validUntil);
        // localStorage aktualisieren, damit Countdown nach Browser-Reload erhalten bleibt
        try {
          if (validUntil) {
            localStorage.setItem(`vnLinkValidUntil_${vorgangId}`, validUntil);
          } else {
            localStorage.removeItem(`vnLinkValidUntil_${vorgangId}`);
          }
        } catch {}
      }
      return res.status;
    } catch {
      return null;
    }
  };

  // Start polling when status is 'wartet'.
  // Interval callbacks check cancelledRef so they never setState after unmount.
  const startPollingIfNeeded = (currentStatus: VnStatus | null) => {
    stopPolling();
    // Bei 'wartet' auf den Link warten; bei 'bereit' weiterpollen, damit der Countdown aktuell
    // bleibt und ein bei BAFA abgelaufener Link (Status kippt auf 'offen') den Button ausgraut.
    if (currentStatus === 'wartet' || currentStatus === 'bereit') {
      pollingRef.current = setInterval(async () => {
        const next = await fetchStatus();
        if (cancelledRef.current) return;
        setStatus(next);
        if (next !== 'wartet' && next !== 'bereit') stopPolling();
      }, 30_000);
    }
  };

  // Effect: load status on mount/activate; start polling if needed.
  // Cleans up polling and marks cancelled on unmount or when active/vorgangId changes.
  useEffect(() => {
    if (!active) {
      stopPolling();
      return;
    }
    cancelledRef.current = false;
    setLoading(true);
    fetchStatus()
      .then(s => {
        if (cancelledRef.current) return;
        setStatus(s);
        startPollingIfNeeded(s);
      })
      .finally(() => {
        if (!cancelledRef.current) setLoading(false);
      });
    return () => {
      cancelledRef.current = true;
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, vorgangId]);

  // Effect: react to status transitions from handleRequest path.
  // When status becomes 'wartet', ensure polling is running.
  useEffect(() => {
    if (status === 'wartet' || status === 'bereit') startPollingIfNeeded(status);
    onStatus?.(status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Sekunden-Takt für den Countdown unterm gelben Knopf, solange auf die BAFA-Mail gewartet wird.
  useEffect(() => {
    if (status !== 'wartet') return;
    const id = setInterval(() => forceTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [status]);

  const formatCountdown = (validUntil: string | null): string | null => {
    if (!validUntil) return null;
    const secs = Math.floor((new Date(validUntil).getTime() - Date.now()) / 1000);
    if (secs <= 0) return null;
    const mm = Math.floor(secs / 60);
    const ss = secs % 60;
    return `${mm}:${String(ss).padStart(2, '0')}`;
  };

  // Klick auf „VN-Link anfordern" → erst Prüf-Dialog mit den aktuellen Werten öffnen.
  const openConfirm = async () => {
    setConfirmOpen(true);
    setConfirmLoading(true);
    setRequestError(null);
    try {
      const d = await getVnRequestData(vorgangId);
      if (!cancelledRef.current) {
        setForm({ vorgangsnummer: d.vorgangsnummer ?? '', plz: d.plz ?? '', email: d.email ?? '' });
        setEmailFromBafaFeld(!!d.emailFromBafaFeld);
      }
    } catch {
      if (!cancelledRef.current) setForm({ vorgangsnummer: '', plz: '', email: '' });
    } finally {
      if (!cancelledRef.current) setConfirmLoading(false);
    }
  };

  const handleRequest = async () => {
    // Dialog schließt sofort — der BAFA-Login dauert bis zu 30s und soll den Klick nicht
    // blockieren. Ergebnis kommt per Status-Polling (Erfolg) bzw. Fehler-Popup (Fehlschlag) nach.
    setConfirmOpen(false);
    setRequesting(true);
    setRequestError(null);
    try {
      await requestVnLink(vorgangId, {
        vorgangsnummer: form.vorgangsnummer.trim(),
        plz: form.plz.trim(),
        email: form.email.trim(),
        notifyCustomer,
      });
      const next = await fetchStatus();
      if (!cancelledRef.current) setStatus(next);
    } catch (e: unknown) {
      setRequestError(e instanceof Error ? e.message : 'Fehler beim Anfordern');
    } finally {
      setRequesting(false);
    }
  };

  const formComplete =
    form.vorgangsnummer.trim() !== '' &&
    form.plz.trim() !== '' &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());

  const confirmModal = confirmOpen
    ? createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          onClick={e => { if (backdropPressRef.current && e.target === e.currentTarget && !requesting) setConfirmOpen(false); }}
          onMouseDown={e => {
            e.stopPropagation();
            backdropPressRef.current = e.target === e.currentTarget;
          }}
        >
          <div
            className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md p-5"
            role="dialog"
            aria-modal="true"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">
              VN-Link bei der BAFA anfordern
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
              Damit meldet sich das System bei der BAFA an — bitte prüfen. Änderungen gelten nur
              für diese Anfrage; eine erfolgreiche E-Mail wird am Vorgang gemerkt.
            </p>
            {confirmLoading ? (
              <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Daten werden geladen…
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <label className="block">
                  <span className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                    Vorgangsnummer <span className="text-slate-400 font-normal">(= Kennung)</span>
                  </span>
                  <input
                    type="text"
                    value={form.vorgangsnummer}
                    onChange={e => setForm(f => ({ ...f, vorgangsnummer: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                    PLZ des Kunden <span className="text-slate-400 font-normal">(= Passwort)</span>
                  </span>
                  <input
                    type="text"
                    value={form.plz}
                    onChange={e => setForm(f => ({ ...f, plz: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                    E-Mail{' '}
                    <span className="text-slate-400 font-normal">
                      {emailFromBafaFeld ? '(am Vorgang gemerkt)' : '(aus den Stammdaten)'}
                    </span>
                  </span>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                  />
                </label>
                {!formComplete && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    Bitte alle drei Felder ausfüllen (E-Mail als gültige Adresse).
                  </p>
                )}
                <label className="flex items-start gap-2 pt-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notifyCustomer}
                    onChange={e => setNotifyCustomer(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-xs text-slate-600 dark:text-slate-300">
                    Kunden per Mail informieren: BAFA-Mail <strong>nicht anklicken</strong>, sondern an
                    uns weiterleiten.
                  </span>
                </label>
              </div>
            )}
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setConfirmOpen(false)}
                disabled={requesting}
                className="px-4 py-2 text-sm font-medium rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-60"
              >
                Abbrechen
              </button>
              <button
                onClick={handleRequest}
                disabled={requesting || confirmLoading || !formComplete}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-60"
              >
                {requesting && <Loader2 className="w-4 h-4 animate-spin" />}
                Stimmt – jetzt anfordern
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  const handleOpen = async () => {
    setOpening(true);
    setOpenError(null);
    try {
      const { ottLink } = await openVnLink(vorgangId);
      // Convert BAFA URL → proxy URL
      const proxyUrl = ottLink.replace('https://fms.bafa.de', `${API_BASE}/api/bafa-proxy`);
      // localStorage statt sessionStorage: window.open(..., 'noopener') kappt die Verbindung
      // zum Opener, wodurch der neue Tab ein LEERES sessionStorage erhält. localStorage ist
      // origin-weit geteilt; das Formular liest es und löscht es sofort wieder.
      localStorage.setItem('vnOttUrl', proxyUrl);
      localStorage.setItem('vnCustomerId', String(customerId));
      localStorage.setItem('vnVorgangId', String(vorgangId));
      window.open('/vn-formular', '_blank', 'noopener');
      // Reload status (link was consumed → back to 'offen')
      const next = await fetchStatus();
      if (!cancelledRef.current) setStatus(next);
    } catch {
      setOpenError('Link konnte nicht geöffnet werden – ggf. erneut anfordern.');
    } finally {
      if (!cancelledRef.current) setOpening(false);
    }
  };

  // VN-Meldungen (Anfordern/Öffnen) als Popup statt winzigem Inline-Text.
  const errorMsg = requestError || openError;
  const clearError = () => { setRequestError(null); setOpenError(null); };
  const errorModal = errorMsg
    ? createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          onClick={e => { if (backdropPressRef.current && e.target === e.currentTarget) clearError(); }}
          onMouseDown={e => {
            e.stopPropagation();
            backdropPressRef.current = e.target === e.currentTarget;
          }}
        >
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <span className="text-2xl leading-none">⚠️</span>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">Verwendungsnachweis</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-line">{errorMsg}</p>
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <button
                onClick={clearError}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-purple-600 text-white hover:bg-purple-700"
              >
                Verstanden
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  // Im „⋯"-Menü sieht der Knopf aus wie jeder andere Menüpunkt; sonst bleibt er,
  // wie er war. Nur Optik — Zustände und Abläufe sind identisch.
  const imMenue = variant === 'menu';
  const menuKlassen = 'w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left transition-colors disabled:opacity-60';
  const huelle = imMenue ? 'block w-full' : 'inline-flex flex-col items-start gap-0.5';
  const hinweis = imMenue ? 'block px-3 pb-1 text-[12px] text-slate-500 dark:text-slate-400' : 'text-[12px] text-slate-500 dark:text-slate-400 px-0.5';

  if (loading && status === null) {
    return (
      <span className={`${imMenue ? menuKlassen : 'inline-flex items-center gap-1 px-2.5 py-1 text-xs'} text-slate-400`}>
        <Loader2 className="w-3 h-3 animate-spin" />
      </span>
    );
  }

  // Status 'bereit' — green upload button
  if (status === 'bereit') {
    return (
      <div className={huelle}>
        <button
          onClick={handleOpen}
          disabled={opening}
          className={imMenue
            ? `${menuKlassen} text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-slate-600 font-medium`
            : 'inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50 disabled:opacity-60'}
          title="VN-Link öffnen und Verwendungsnachweis hochladen"
        >
          {opening ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span>🟢</span>}
          VN HOCHLADEN
        </button>
        {(() => {
          const mins = linkValidUntil ? Math.ceil((new Date(linkValidUntil).getTime() - Date.now()) / 60000) : null;
          return mins != null && mins > 0
            ? <span className={hinweis}>noch {mins} Min gültig – jetzt öffnen</span>
            : null;
        })()}
        {errorModal}
      </div>
    );
  }

  // Status 'wartet' — gelb, aber klickbar: erneut anfordern (falls die erste Mail nicht kam)
  if (status === 'wartet') {
    return (
      <div className={huelle}>
        <button
          onClick={openConfirm}
          disabled={requesting}
          className={imMenue
            ? `${menuKlassen} text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-slate-600`
            : 'inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/40 disabled:opacity-60'}
          title="Warte auf BAFA-Mail (Postfach wird automatisch geprüft). Keine Mail erhalten? Klicken, um den Einmal-Link erneut anzufordern."
        >
          {requesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span>🟡</span>}
          VN-Link angefordert
        </button>
        {(() => {
          const countdown = formatCountdown(linkValidUntil);
          return countdown ? <span className={hinweis}>noch {countdown} Min gültig</span> : null;
        })()}
        {confirmModal}
        {errorModal}
      </div>
    );
  }

  // Roher VN liegt bereits im Vorgang → „VN-Link anfordern" ausblenden (Formular ist schon da,
  // erneutes Anfordern bei der BAFA nicht nötig). Betrifft nur den 'offen'-Zustand; ein bereits
  // laufender Link-Vorgang ('wartet'/'bereit') wird oben behandelt und bleibt sichtbar.
  if (vnUploaded) return null;

  // Status 'offen' (or null/unknown) — grey button to request link directly
  return (
    <div className={huelle}>
      <button
        onClick={openConfirm}
        disabled={requesting}
        className={imMenue
          ? `${menuKlassen} text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600`
          : 'inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-60'}
        title="VN-Link bei BAFA anfordern"
      >
        {requesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
        VN-Link anfordern
      </button>
      {confirmModal}
      {errorModal}
    </div>
  );
}
